import { useSupportUserStatus } from "@src/features/tenants";
import { FEATURE_FLAGS, useBooleanFlag } from "@src/services/feature-flags/feature-flags";
import { BACKFILL_SUBPATH, RECOMPUTE_INCIDENTS_SUBPATH, type PatriotTool } from "./routes";

/**
 * Whether the sidebar offers Patriot tools: support users, behind the
 * `patriot_tools` flag.
 *
 * The flag only decides whether the entry is offered. The routes gate on the
 * support role alone (see the section's `_layout.web.tsx`), and the API gates on
 * it again server-side, so the flag grants nothing by itself.
 *
 * `useBooleanFlag` rather than `getBooleanFlag` so the React Compiler cannot
 * freeze the answer at its first-render value — see `useClipCompilationAccess`.
 */
export function useShowPatriotToolsInSidebar(): boolean {
  const isEnabled = useBooleanFlag(FEATURE_FLAGS.PATRIOT_TOOLS);
  const supportStatus = useSupportUserStatus();
  return isEnabled && supportStatus === "granted";
}

/**
 * Whether the support user may use the Backfill page: the
 * `patriot_tools_backfill` flag, which the API also checks server-side.
 */
export function useCanStartPatriotBackfill(): boolean {
  return useBooleanFlag(FEATURE_FLAGS.PATRIOT_TOOLS_BACKFILL);
}

/**
 * Whether the support user may use the Recompute incidents page: the
 * `monitoring_incident_recompute` flag, which the API also checks server-side.
 */
export function useCanRecomputeIncidents(): boolean {
  return useBooleanFlag(FEATURE_FLAGS.MONITORING_INCIDENT_RECOMPUTE);
}

/** Whether the landing page lists `tool`: every tool, except Backfill and Recompute incidents while their flags are off. */
export function useIsPatriotToolOffered(): (tool: PatriotTool) => boolean {
  const canStartBackfill = useCanStartPatriotBackfill();
  const canRecomputeIncidents = useCanRecomputeIncidents();
  return (tool) => {
    if (tool.subpath === BACKFILL_SUBPATH) return canStartBackfill;
    if (tool.subpath === RECOMPUTE_INCIDENTS_SUBPATH) return canRecomputeIncidents;
    return true;
  };
}
